import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { useDirtySections } from '@/components/profile';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useNotify, useToggle } from '@/hooks';
import { useAuthStore } from '@/store/useAuthStore';
import { sectionOfField } from '../profileSections';

/**
 * Form instance, profile-section helpers, dirty tracking, drawers, loading the employee into the form and the field permissions.
 */
export default function useEmployeeFormBase({
  formData,
  formProp,
  onAdditionalInfoDirtyChange,
  onDirtySectionsChange,
  profileSection,
  readOnly,
}) {
  const notification = useNotify();
  const navigate = useNavigate();

  const {
    queryParams: { company },
  } = useQueryParamsContext();

  const { t } = useTranslation(['forms', 'messages', 'translations', 'tables']);
  const [refetchCompany, setRefetchCompany] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [deletedFileIds, setDeletedFileIds] = useState([]);
  const [showAdditionalDays, setShowAdditionalDays] = useState(false);
  const currentUser = useAuthStore((state) => state.user);
  const isEmployee =
    currentUser?.roles?.includes('employee') &&
    currentUser?.roles?.length === 1;

  const [ownForm] = Form.useForm();
  const form = formProp || ownForm;
  const queryClient = useQueryClient();
  const isProfile = !!profileSection;
  // Field / block visibility on the profile page; always visible elsewhere.
  const inSection = (id) => !isProfile || profileSection === id;
  const sectionClass = (id) => (inSection(id) ? '' : 'hidden');
  const [uploadKey, setUploadKey] = useState(0);
  // Form values as loaded; on the profile page Save sends only what differs.
  const baselineRef = useRef(null);

  useDirtySections({
    form,
    baselineRef,
    enabled: isProfile,
    sectionOfField,
    onChange: onDirtySectionsChange,
    extraSections: selectedFiles.length || deletedFileIds.length ? ['dok'] : [],
  });
  const {
    open: drawerOpen,
    onToggle: toggleDrawer,
    onClose: closeDrawer,
  } = useToggle();
  const {
    open: historyOpen,
    setOpen: setHistoryOpen,
    onClose: onHistoryClose,
  } = useToggle(false);
  const [resetTrigger, setResetTrigger] = useState(0);

  const handleAdditionalInfoDirtyChange = (dirty) => {
    if (onAdditionalInfoDirtyChange) {
      onAdditionalInfoDirtyChange(dirty);
    }
  };

  const transformBankAccountValue = (value) => {
    const num = Number(value);
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      Number.isNaN(num)
    ) {
      return '';
    }

    return value;
  };

  useEffect(() => {
    if (formData) {
      const formValues = {
        ...formData,
        generalData: {
          ...formData.generalData,
          bankAccounts: {
            part1: transformBankAccountValue(
              formData?.generalData?.bankAccounts?.part1
            ),
            part2: transformBankAccountValue(
              formData?.generalData?.bankAccounts?.part2
            ),
            part3: transformBankAccountValue(
              formData?.generalData?.bankAccounts?.part3
            ),
          },
        },
        // Profile holds only the absence quota (enabled + per-type limits).
        // Entries are managed in the calendar, not here.
        specialAbsence: {
          enabled: formData.specialAbsence?.enabled ?? false,
          limits: {
            absence: 0,
            sick: 0,
            other: 0,
            ...(formData.specialAbsence?.limits || {}),
          },
        },
      };

      if (formData.additionalPersonalInfo) {
        formValues.additionalPersonalInfo = {
          ...formData.additionalPersonalInfo,
          birthDate: formData.additionalPersonalInfo.birthDate
            ? dayjs(formData.additionalPersonalInfo.birthDate)
            : undefined,
          familyCelebrationDate: formData.additionalPersonalInfo
            .familyCelebrationDate
            ? dayjs(formData.additionalPersonalInfo.familyCelebrationDate)
            : undefined,
          workExperienceStartDate: formData.additionalPersonalInfo
            .workExperienceStartDate
            ? dayjs(formData.additionalPersonalInfo.workExperienceStartDate)
            : undefined,
          previousEmployments:
            formData.additionalPersonalInfo.previousEmployments?.map((emp) => ({
              ...emp,
              startDate: emp.startDate ? dayjs(emp.startDate) : null,
              endDate: emp.endDate ? dayjs(emp.endDate) : null,
            })) || [],
          children:
            formData.additionalPersonalInfo.children?.map((child) => ({
              ...child,
              birthDate: child.birthDate ? dayjs(child.birthDate) : null,
            })) || [],
        };
      }

      // Convert suspensionVariationsDates to dayjs for DatePicker
      const svDates = formValues.otherAllowances?.suspensionVariationsDates;
      if (svDates && typeof svDates === 'object') {
        const converted = {};
        for (const [key, val] of Object.entries(svDates)) {
          converted[key] = {
            validFrom: val?.validFrom ? dayjs(val.validFrom) : undefined,
            validTo: val?.validTo ? dayjs(val.validTo) : undefined,
          };
        }
        formValues.otherAllowances = {
          ...formValues.otherAllowances,
          suspensionVariationsDates: converted,
        };
      }

      form.setFieldsValue(formValues);
      // The store is replaced immutably on every change, so this reference
      // keeps the loaded values.
      baselineRef.current = form.getFieldsValue(true);
    }
  }, [formData, form]);

  // Read-only profile view: every field is locked, whatever the server allows.
  // Fields pass their own `disabled`, which wins over the form-level flag.
  const formFields = useMemo(() => {
    const fields = formData?.EMPLOYEE_GENERAL_DATA_FORM_FIELDS;
    if (!readOnly || !fields) return fields;
    return Object.fromEntries(
      Object.entries(fields).map(([key, state]) => [
        key,
        { ...state, disabled: true },
      ])
    );
  }, [formData?.EMPLOYEE_GENERAL_DATA_FORM_FIELDS, readOnly]);

  return {
    baselineRef,
    closeDrawer,
    company,
    deletedFileIds,
    drawerOpen,
    form,
    formFields,
    handleAdditionalInfoDirtyChange,
    historyOpen,
    inSection,
    isEmployee,
    isProfile,
    navigate,
    notification,
    onHistoryClose,
    queryClient,
    refetchCompany,
    resetTrigger,
    sectionClass,
    selectedFiles,
    setDeletedFileIds,
    setHistoryOpen,
    setRefetchCompany,
    setResetTrigger,
    setSelectedFiles,
    setShowAdditionalDays,
    setUploadKey,
    showAdditionalDays,
    t,
    toggleDrawer,
    uploadKey,
  };
}
